"use client";

import { useEffect, useState } from "react";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { getPastExamCompletionCounts } from "@/lib/server-actions/pastExamCompletionActions";
import { createClientTtlCache } from "@/lib/client-ttl-cache";

/** past-exam 회차 href("/past-exam/2024/2024-1") → roundId("2024-1") */
export function roundIdFromHref(href: string): string | null {
  const last = href.split("/").filter(Boolean).pop();
  return last && /^\d{4}-\d+$/.test(last) ? last : null;
}

/** 완주 데이터 스냅샷 — 회차별 완주 횟수 + 전체 회차 수/목록(진행상황 분모·박스, JCG-2263). */
interface CompletionData {
  counts: Record<string, number>;
  totalRounds: number;
  allRounds: string[];
}

// ── 클라이언트 TTL 캐시 + in-flight 디듀프 (JCG-2242 #1) ─────────────────────
// 모듈 스코프: 탭 1개당 1개, 클라이언트 네비게이션 동안 유지(F5 시 초기화).
// 과거회차 그리드(루트 + 연도 7페이지)를 넘길 때마다 같은 결과를 재쿼리하던 낭비를
// 60초당 1회로 묶는다. 탭 내엔 로그인 사용자 1명 + 활성 시즌 1개뿐이라 키 없이 안전.
// 실패는 캐시하지 않는다: 빈 스냅샷을 흘려보내면 hook 의 loaded 가 non-null 이 되어 showCompletion=true 로
// 바뀌고, 완주한 회차까지 전부 빨간 "!"(미완주)로 표시되므로(로딩 가드 무력화) null 로 로딩 상태를 유지한다.
const completionCache = createClientTtlCache<CompletionData>(async () => {
  const result = await getPastExamCompletionCounts();
  return result.success
    ? {
        counts: result.counts,
        totalRounds: result.totalRounds,
        allRounds: result.allRounds,
      }
    : null;
}, 60_000);

/**
 * 클라이언트 완주 캐시 무효화 (JCG-2242 #1).
 * 과거회차 문제를 푼 뒤 그리드로 복귀할 때 호출하면, 60초 TTL을 기다리지 않고 즉시 최신값을
 * 다시 불러온다(서버 캐시는 saveProblemHistory의 revalidateTag로 무효화됨, SWR, #2).
 */
export function clearPastExamCompletionCache(): void {
  completionCache.clear();
}

/**
 * 기출 회차 완주 횟수 로드 훅 (프리미엄 이상만). JCG-2242
 *
 * past-exam 회차 카드를 렌더하는 컴포넌트(FolderGrid·PastExamIconGrid)가 공유한다.
 * 출석(attendance)과 별개 데이터이며, 비프리미엄/비로그인/오류 시 빈 결과로 안전.
 *
 * @param enabled false면 조회하지 않는다(기본 true). 과목 그리드 등 비-past-exam은 false.
 */
export function usePastExamCompletion(enabled = true) {
  const { isPremiumOrAbove } = useEffectivePass();
  // 로드 대상 여부(프리미엄 + past-exam). 표시(showCompletion)는 데이터 로드 후로 미룬다.
  const active = isPremiumOrAbove && enabled;
  // 완주 데이터 스냅샷. null = 아직 미로드.
  // 로드 전 빈 맵으로 노출하면 모든 회차가 "미완주 !"로 깜빡이므로, null로 로딩을 구분한다.
  const [loaded, setLoaded] = useState<CompletionData | null>(null);

  useEffect(() => {
    // effect 트리거는 active(=isPremiumOrAbove && enabled)에만 의존.
    // showCompletion(loaded 의존)을 deps로 쓰면 setLoaded→재실행 루프가 생기므로 금지.
    if (!active) return;
    let cancelled = false;
    // 모듈 TTL 캐시 경유 — 60초 내 다른 과거회차 페이지 마운트는 쿼리 없이 즉시 반환.
    completionCache.load().then((data) => {
      if (!cancelled) setLoaded(data);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  // 데이터 로드 완료(loaded !== null) 후에만 뱃지를 표시 → 로딩 중 빨간 ! 깜빡임 방지.
  const showCompletion = active && loaded !== null;
  const completionByRound = loaded?.counts ?? {};
  // 진행상황(JCG-2263)용: 완주한 회차 수 / 전체 회차 수 / 전체 회차 목록(박스 그리드).
  const completedRoundCount = Object.keys(completionByRound).length;
  const totalRoundCount = loaded?.totalRounds ?? 0;
  const allRounds = loaded?.allRounds ?? [];

  /** 회차 href의 완주 횟수 (0 = 미완주 / 회차 아님). */
  const completionForHref = (href: string): number => {
    const roundId = roundIdFromHref(href);
    return roundId ? (completionByRound[roundId] ?? 0) : 0;
  };

  return {
    showCompletion,
    completionByRound,
    completionForHref,
    completedRoundCount,
    totalRoundCount,
    allRounds,
  };
}
